/**
 * @class paychex.app.landing.shared.messageList.MessageListModelService
 * @description
 * Wraps the MessageListModel and provides access to the data
 */
define('paychex.app.messages/services/MessageListModelService',[
    'lodash',
    'jquery',
    'lib.rx',
    'paychex.core/Errors',
    'paychex.core/Logger',
    'paychex.core/Promise',
    'paychex.core/Tracker',
    'paychex.core/ModelList',
    'paychex.core/Workflows',
    'paychex.app.messages/messages/MessageStatus173',
    'paychex.app.messages/messages/MessageStatus174',
    'paychex.app.common/data/userInfo/helper/UserInfoHelper',
    'paychex.topics.common'
],
    function (_,
              $,
              Rx,
              Errors,
              Logger,
              Promise,
              Tracker,
              ModelList,
              Workflows,
              MessageStatus173,
              MessageStatus174,
              UserInfoHelper) {

        'use strict';

        var ngDependencies = [
                '$q',
                '$timeout',
                '$rootScope',
                'MessageListDataService',
                'INotificationService',
                'ParseMarkupService',
                'MessageListPdfGenerationService',
                '$sce',
                'UserInfoService',
                'GlobalStateService',
                'ProgressService',
                'UrlService'
            ],
            Constructor = function ($q, $timeout, $rootScope, MessageListDataService, INotificationService, ParseMarkupService, MessageListPdfGenerationService, $sce, UserInfoService, GlobalStateService, ProgressService, UrlService) {
                var helper,

                    /**
                     * @constant
                     * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                     * @private
                     * @type {Object}
                     * @property {String} NAMESPACE the namespace for this service
                     * @property {String} GENERAL_ID the "company ID" of the general messages
                     * @property {String} GENERAL_DISPLAY_NAME the text to display for the general message shade
                     * @property {Object} PRIORITY an object containing the message priorities: HIGH, NORMAL, and LOW
                     * @property {Object} STATUS an object containing the message statuses: READ and UNREAD
                     * @description
                     * Object containing all the constants for this service
                     */
                    Constants = {
                        NAMESPACE: 'paychex.app.landing.shared.messageList.MessageListModelService',
                        GENERAL_ID: '_GENERAL_',
                        GENERAL_DISPLAY_NAME: 'General',
                        MAX_CLIENTS: 5,
                        PRIORITY: {
                            HIGH: 'HIGH',
                            NORMAL: 'NORMAL',
                            LOW: 'LOW'
                        },
                        STATUS: {
                            READ: 'READ',
                            UNREAD: 'UNREAD'
                        }

                    },
                    currentGroupBeingProcessed = null,
                    readyToLoadMessagePromise = $q.defer(),
                    log = Logger.get(Constants.NAMESPACE, {level: 'ALL'}),
                    internal,

                    // external API
                    external = {
                        /**
                         * @ngdoc method
                         * @function getMessageGroupById
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @param {string} clientId - unique client ID
                         * @returns {Object|null} the selected group, or null
                         * @description checks the ID provided and returns the group if it exists; otherwise, returns null
                         */
                        getMessageGroupById: function getMessageGroupById(clientId) {
                            var matchedGroup = _.filter(internal.messageGroups.getItems(), {clientId: clientId});
                            return matchedGroup.length === 1 ? matchedGroup[0] : null;
                        },
                        /**
                         * @ngdoc method
                         * @function moveToNextMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @description
                         * moves to the next message in the list
                         */
                        moveToNextMessage: function moveToNextMessage() {
                            if (!internal.messageGroups.getActive().messageList.moveNext()) {
                                internal.messageGroups.getActive().messageList.setActive(null);
                                while (internal.messageGroups.moveNext() || !internal.messageGroups.atEnd) {
                                    // Added to fix 103579- if there is only 1 message in a group, MoveNext skips it after first time.
                                    //  Currently, after first time, both atEnd/atStart get set to true, and next time it will skip.
                                    //  This fix will reset atEnd to false
                                    if (internal.messageGroups.getActive().messageList.getSize() === 1) {
                                        internal.messageGroups.getActive().messageList.setActive(null);
                                    }
                                    if (internal.messageGroups.getActive().messageList.moveNext()) {
                                        break;
                                    }
                                }

                            }
                        },
                        /**
                         * @ngdoc method
                         * @function moveToPreviousMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @description
                         * moves to the previous message in the list
                         */
                        moveToPreviousMessage: function moveToPreviousMessage() {
                            if (!internal.messageGroups.getActive().messageList.movePrev()) {
                                internal.messageGroups.getActive().messageList.setActive(null);
                                while (internal.messageGroups.movePrev() || !internal.messageGroups.atStart) {
                                    // Added to fix 103579- if there is only 1 message in a group, MovePrev skips it after first time.
                                    //  Currently, after first time, both atEnd/atStart get set to true, and next time it will skip.
                                    //  This fix will reset atEnd to false
                                    if (internal.messageGroups.getActive().messageList.getSize() === 1) {
                                        internal.messageGroups.getActive().messageList.setActive(null);
                                    }
                                    if (internal.messageGroups.getActive().messageList.movePrev(true)) {
                                        break;
                                    }
                                }
                            }
                        },
                        /**
                         * @ngdoc method
                         * @function getActiveGroup
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Object} group the currently active group
                         * @description
                         * gets the currently active group
                         */
                        getActiveGroup: function getActiveGroup() {
                            return internal.activeGroup;
                        },
                        /**
                         * @ngdoc method
                         * @function setActiveGroup
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @param {Object} group the group to set active
                         * @description
                         * sets the currently active group
                         * @returns {function} getActiveGroup - retrieves the newly selected active message group
                         */
                        setActiveGroup: function setActiveGroup(group) {
                            internal.messageGroups.setActive(group);
                            return external.getActiveGroup();
                        },
                        /**
                         * @ngdoc method
                         * @function selectMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @param {Object} message the message whose state just changed
                         * @returns {String} the new state of a tri-state checkbox
                         * @description
                         * updates the selection based on the single message and its state. Will return the
                         * correct state of a tri-state checkbox based on the active group's message list
                         */
                        selectMessage: function selectMessage(message) {

                            if (internal.activeGroup) {
                                internal.activeGroup.messageList.toggleSelected(message);

                                // TODO: remove once the tri-state checkbox has been implemented
                                var currentSelection = internal.activeGroup.messageList.getSelected(),
                                    selectedCount = currentSelection.length,
                                    groupCount = internal.activeGroup.messageList.getSize();
                                external.checkboxState = internal.checkboxState(selectedCount, groupCount);
                                return currentSelection;
                            }
                            return null;
                        },
                        /**
                         * @ngdoc method
                         * @function selectByStatus
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @param {String} status the status by which to select
                         * @returns {String} the new state of a tri-state checkbox
                         * @description
                         * updates the selection based on a message status. Will return the
                         * correct state of a tri-state checkbox based on the active group's message list
                         */
                        selectByStatus: function selectByStatus(status) {
                            if (internal.activeGroup) {
                                var newSelection;
                                switch (status) {
                                    case 'all':
                                        newSelection = internal.activeGroup.messageList.getItems();
                                        break;
                                    case 'unread':
                                        newSelection = _.filter(internal.activeGroup.messageList.getItems(), {readStatus: Constants.STATUS.UNREAD});
                                        break;
                                    case 'read':
                                        newSelection = _.filter(internal.activeGroup.messageList.getItems(), {readStatus: Constants.STATUS.READ});
                                        break;
                                    default:
                                        newSelection = [];
                                        break;
                                }
                                internal.activeGroup.messageList.setSelected(newSelection);
                                var currentSelection = internal.activeGroup.messageList.getSelected(); // eslint-disable-line one-var
                                var totalGroupCount = internal.activeGroup.messageList.getSize(); // eslint-disable-line one-var


                                // TODO remove once the tri-state checkbox has been implemented
                                external.checkboxState = internal.checkboxState(currentSelection.length, totalGroupCount);
                                return currentSelection;
                            }
                            return null;
                        },
                        /**
                         * @ngdoc method
                         * @function deleteSelectedMessages
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * deletes the messages that are currently selected (checked in the UI)
                         */
                        deleteSelectedMessages: function deleteSelectedMessages() {
                            if (internal.activeGroup) {
                                return internal.deleteWorkflow.dispatch([internal.activeGroup.messageList.getSelected()], {
                                    tags: ['delete'],
                                    label: 'Delete Messages',
                                    category: 'ListPanel: Messages'
                                });
                            }
                            return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function markSelectedMessagesRead
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * marks the messages that are currently selected (checked in the UI) as read
                         */
                        markSelectedMessagesRead: function markSelectedMessagesRead() {
                            if (internal.activeGroup) {
                                return internal.markReadAndDeselectWorkflow.dispatch([internal.activeGroup.messageList.getSelected()], {
                                    category: 'ListPanel: Messages',
                                    label: 'Mark Messages Read'
                                });
                            }
                            return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function markSelectedMessagesUnread
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * marks the messages that are currently selected (checked in the UI) as unread
                         */
                        markSelectedMessagesUnread: function markSelectedMessagesUnread() {
                            if (internal.activeGroup) {
                                return internal.markUnreadAndDeselectWorkflow.dispatch([internal.activeGroup.messageList.getSelected()], {
                                    category: 'ListPanel: Messages',
                                    label: 'Mark Messages Unread'
                                });
                            }
                            return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function downloadSelectedMessages
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @description
                         *  Currently selected message will be send to generate PDF to PDFGenerationService
                         **/
                        downloadSelectedMessages: function downloadSelectedMessages() {
                            if (internal.activeGroup) {
                                MessageListPdfGenerationService.generateMessagesPdf(internal.activeGroup.messageList.getSelected(), internal.activeGroup);
                            }
                            // return $q.reject('no active group');
                        },

                        /** @ngdoc method
                         * @function setActiveMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @param {Object} newActiveMessage - the new message to be set as active
                         * @description
                         * returns the newly active message
                         * @returns {function} getActiveMessage - retrieves currently active message
                         */
                        setActiveMessage: function setActiveMessage(newActiveMessage) {
                            if (internal.activeGroup) {
                                internal.activeGroup.messageList.setActive(newActiveMessage);
                            }
                            // ensure that there is only one active at a time
                            // case: activate a message (open in drawer).
                            //       select a different company
                            //       active a different message
                            //       switch back to original company (still would be "active" but not in drawer)
                            if (internal.lastActiveGroup) {
                                internal.lastActiveGroup.messageList.setActive();
                            }
                            // TODO return the actual active message (from first if-block) or null
                            return external.getActiveMessage();
                        },
                        /**
                         * @ngdoc method
                         * @function getActiveMessageIndex
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {number}the currently active message index
                         * @description
                         * returns the newly active message index
                         */
                        getActiveMessageIndex: function getActiveMessageIndex() {
                            var currentActiveIndex = internal.activeGroup.messageList.getItems().indexOf(internal.activeGroup.messageList.getActive()) + 1;
                            if (_.head(internal.messageGroups.getItems()) === internal.activeGroup) {
                                return currentActiveIndex;
                            } else {
                                _.forEach(_.slice(internal.messageGroups.getItems(), 0, internal.messageGroups.getItems().indexOf(internal.activeGroup)), function updateActiveIndex(group) {
                                    var messages = group.messageList.getItems();
                                    currentActiveIndex += messages.length;
                                });
                                return currentActiveIndex;
                            }
                        },
                        /**
                         * @ngdoc method
                         * @function getActiveMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Object} the currently active message
                         * @description
                         * returns the newly active message
                         */
                        getActiveMessage: function getActiveMessage() {
                            if (internal.activeGroup) {
                                return {
                                    message: internal.activeGroup.messageList.getActive(),
                                    hasPrevious: internal.checkForPreviousMessage(internal.activeGroup),
                                    hasNext: internal.checkForNextMessage(internal.activeGroup),
                                    activeMessageIndex: external.getActiveMessageIndex(),
                                    totalMessageCount: external.totalCount
                                };
                            }
                            return null;
                        },
                        /**
                         * @ngdoc method
                         * @function deleteActiveMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * deletes the currently active message
                         */
                        deleteActiveMessage: function deleteActiveMessage() {
                            if (internal.activeGroup) {
                                return internal.deleteWorkflow.dispatch([
                                    [internal.activeGroup.messageList.getActive()], {
                                        category: 'ListPanel: Messages',
                                        label: 'Delete Selected Messages'
                                    }
                                ]);
                            }
                            return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function markActiveMessageRead
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * marks the currently active message read
                         */
                        markActiveMessageRead: function markActiveMessageRead() {
                            if (internal.activeGroup) {
                                return internal.markReadWorkflow.dispatch([
                                    [internal.activeGroup.messageList.getActive()], {
                                        category: 'ListPanel: Messages',
                                        label: 'Mark Message Read'
                                    }
                                ]);
                            }
                            return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function markActiveMessageUnread
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * marks the currently active message unread
                         */
                        markActiveMessageUnread: function markActiveMessageUnread() {
                            if (internal.activeGroup) {
                                return internal.markUnreadWorkflow.dispatch([
                                    [internal.activeGroup.messageList.getActive()], {
                                        category: 'ListPanel: Messages',
                                        label: 'Mark Message Unread'
                                    }
                                ]);
                            }
                            return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function downloadActiveMessage
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @description
                         * downloads the currently active message and returns a promise that will be resolved/rejected as part of the workflow
                         */
                        downloadActiveMessage: function downloadActiveMessage() {
                            if (internal.activeGroup) {
                                MessageListPdfGenerationService.generateMessagesPdf([internal.messageGroups.getActive().messageList.getActive()], internal.activeGroup);
                            }
                            // return $q.reject('no active group');
                        },
                        /**
                         * @ngdoc method
                         * @function setReady
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @description
                         * controling the start point of loading messages
                         */
                        setReady: function setReady() {
                            readyToLoadMessagePromise.resolve('ready');
                        },
                        /**
                         * @ngdoc method
                         * @function refreshMessages
                         * @param {Object} newState - new state of the messages
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @returns {Promise} a promise that will be resolved/rejected as part of the workflow
                         * @description
                         * dispatches the workflow to refresh the message list
                         */
                        refreshMessages: function refreshMessages(newState) {
                            var updateState = newState;
                            if (!updateState) {
                                updateState = GlobalStateService.getState();
                            }
                            internal.currentState = internal.makeSimpleState(updateState);
                            external.isRefreshed = true;
                            external.refreshMessagePromise = internal.refreshMessagesWorkflow.dispatch();
                            return external.refreshMessagePromise;
                        },

                        loadMessages: function loadMessages(newState) {
                            var updateState = newState;
                            if (!updateState) {
                                updateState = GlobalStateService.getState();
                            }
                            updateState = internal.makeSimpleState(updateState);
                            if (internal.checkIfStateChanged(updateState)) {
                                internal.currentState = updateState;
                                external.getMessagePromise = internal.getMessageWorkflow.dispatch();
                            }
                            return external.getMessagePromise;
                        },

                        /**
                         * Cache messages after the initial remote call to avoid further calls when the My Account / Company modes are switched
                         */

                        localRefreshMessages: function localRefreshMessages() {
                            return internal.localRefreshMessagesWorkflow.dispatch();
                        },
                        /**
                         * @ngdoc method
                         * @function sortMessages
                         * @memberOf paychex.app.landing.shared.messageList.MessageListModelService
                         * @param {Array} [properties] the array of properties to sort by
                         * @param {number} [order] the order of the primary property (1 = ascending, -1 = descending)
                         * @description
                         * configures the sort function by which each group's message list is sorted
                         */
                        sortMessages: function sortMessages(properties, order) {
                            var sort = properties,
                                sequence = order,
                                lastProperties = internal.sortHelpers.defaults.properties,
                                lastOrder = internal.sortHelpers.defaults.order,
                                sortFns,
                                masterSort;

                            sort = lastProperties = sort || lastProperties;
                            sequence = lastOrder = sequence || lastOrder;
                            sortFns = _.map(sort, function sortFunctions(prop, index) {
                                return _.partial(internal.sortHelpers.genericSortFn,
                                        prop,
                                        (index === 0 ? sequence : internal.sortHelpers.defaults.sorts[prop]),
                                        internal.sortHelpers.converterMaps[prop] || _.identity);
                            });
                            masterSort = function mainSortFn(a, b) {
                                var i = 0,
                                    result = sortFns[i](a, b);
                                while (result === 0 && i < sortFns.length - 1) {
                                    result = sortFns[++i](a, b);
                                }
                                return result;
                            };

                            _.forEach(internal.messageGroups.getItems(), function sortMessageList(group) {
                                group.messageList.sortBy(masterSort);
                            });
                        },
                        on: function (mode, event, callback) {
                            switch (mode) {
                                case 'messageList':
                                    var deregisters = {};
                                    _.forEach(internal.messageGroups.getItems(), function changeClientId(group) {
                                        deregisters[group.clientId] = group.messageList.on(event, callback);
                                    });
                                    return deregisters;
                                case 'messageGroups':
                                    return internal.messageGroups.on(event, callback);
                                default:
                                    Errors.throw(Errors.Types.IllegalArgument, 'on(mode, event, callback) expects mode to be "messageList" or "messageGroups"');
                                // no break after a throw
                            }
                        },
                        checkIfStateChanged: function checkIfStateChanged(newState) {
                            return internal.checkIfStateChanged(internal.makeSimpleState(newState), internal.currentState);
                        }
                    };
                    // internal data and functions
                internal = {
                        // updates the static (externally facing) model
                    updateStaticModel: function () {
                        external.numberOfGroups = internal.messageGroups.getSize();
                        external.model = [];
                        _.forEach(internal.messageGroups.getItems(), function updateStaticModel(group, index) {
                            var messages = group.messageList.getItems();
                            external.model.push({
                                clientId: group.clientId,
                                displayOrder: index,
                                displayName: group.displayName,
                                messageList: messages,
                                totalCount: messages.length,
                                unreadCount: _.filter(messages, {readStatus: Constants.STATUS.UNREAD}).length
                            });
                        });
                        internal.updateCounts();
                    },

                    updateCounts: function () {

                        var prevTotal = external.totalCount === undefined ? -1 : external.totalCount,
                            prevUnread = external.unreadCount === undefined ? -1 : external.unreadCount;

                        external.totalCount = 0;
                        external.unreadCount = 0;

                        _.forEach(internal.messageGroups.getItems(), function updateCounts(group) {
                            var messages = group.messageList.getItems();
                            external.totalCount += messages.length;
                            external.unreadCount += _.filter(messages, {readStatus: Constants.STATUS.UNREAD}).length;
                        });

                        if (prevTotal !== external.totalCount) {
                            Tracker.static.setMetric('panel', 'Total Message Count', external.totalCount);
                        }

                        if (prevUnread !== external.unreadCount) {
                            Tracker.static.setMetric('panel', 'Unread Message Count', external.unreadCount);
                        }

                    },
                        // set the "checked" property of the newly selected message
                    updateSelectedMessages: function (event) {
                        var newSelection = event.args[0],
                            oldSelection = event.args[1],
                            totalLength = !!external.getActiveGroup() ? external.getActiveGroup().messageList.getSize() : 0;

                            // uncheck those in old but not new
                        _.forEach(_.difference(oldSelection, newSelection), function uncheckDeselect(deselect) {
                            deselect.checked = false;
                        });

                            // check those in new but not in old
                        _.forEach(_.difference(newSelection, oldSelection), function checkSelect(select) {
                            select.checked = true;
                        });

                        external.checkboxState = internal.checkboxState(newSelection.length, totalLength);
                    },
                        // check if there is a previous message
                    checkForPreviousMessage: function (activeGroup) {
                            // simple case: if there are previous messages in the same group
                        if (!activeGroup.messageList.atStart) {
                            return true;
                        } else {
                            var groups = internal.messageGroups.getItems(),
                                activeIndex = _.indexOf(groups, activeGroup);
                                // if activeIndex is 2, we want to check the first two items ([0], [1])

                                // _.first/_.head now returns the first *object* in an array, not an array with 1 object,
                                // which means the callback passed to _.some was sent (value, key, object) arguments instead
                                // of just (value)... which caused an error when trying to access the non-existent
                                // messageList.getSize method

                            return _.some(
                                    _.slice(groups, 0, activeIndex),
                                    _.flow(
                                        _.method('messageList.getSize'),
                                        _.partialRight(_.gt, 0)
                                    ));

                                // OLD:
                                // return _.some(_.first(groups, activeIndex), function (group) {
                                //    return group.messageList.getSize() > 0;
                                // });
                        }
                    },
                        // check if there is a next message
                    checkForNextMessage: function (activeGroup) {
                            // simple case: if there are next messages in the same group
                        if (!activeGroup.messageList.atEnd) {
                            return true;
                        } else {
                            var groups = internal.messageGroups.getItems(),
                                nextIndex = _.indexOf(groups, activeGroup) + 1;

                                // if active is 1, then next is 2, we want to check all but the first two ([0], [1])

                                // in lodash v4, _.rest simulates the new ES6 rest params feature, so it
                                // expects a function argument and some optional initial arguments; it doesn't
                                // have anything to do with arrays now

                            return _.some(
                                    _.slice(groups, nextIndex),
                                    _.flow(
                                        _.method('messageList.getSize'),
                                        _.partialRight(_.gt, 0)
                                    ));

                                // OLD:
                                // return _.some(_.rest(internal.messageGroups.getItems(), nextIndex), function (group) {
                                //    return group.messageList.getSize() > 0;
                                // });
                        }
                    },
                        // return the unique ID
                    getMessageValue: function () {
                        return this.hashMessageKey;
                    },
                    convertFML: function (messages) {
                        _.forEach(messages, function convertFML(msg){
                            var result = _.attempt(function parseMessage(){
                                if ((!_.isObject(msg.message))) {
                                    msg.message = $sce.trustAsHtml(ParseMarkupService.parseMarkup(msg.message));
                                }
                            });
                            if(_.isError(result)){
                                log.info('message failed during parsing: ', msg.message);
                            }
                            return result;
                        });
                    },
                        // create the "group" object: {clientId, displayName, unreadCount, and messageList}
                    createGroupObject: function (group) {
                        var clientMessageList = new ModelList();

                            // TODO make call to set up listeners on the individual messages
                            // items-modified is a way for the user to tell the collection it has updated
                        clientMessageList.on('items-modified, items-added', function modifyItems(e) {
                            currentGroupBeingProcessed = group.clientId;
                            internal.convertFML(e.args[0]);
                        });

                        clientMessageList.add(group.messages);
                        clientMessageList.setValueOf(internal.getMessageValue);
                        clientMessageList.on('collection-changed, items-modified', internal.updateStaticModel);
                        clientMessageList.on('active-changed', internal.updateActiveMessage);
                        clientMessageList.on('selection-changed', internal.updateSelectedMessages);

                        return {
                            clientId: group.clientId ? group.clientId : Constants.GENERAL_ID,
                            displayName: group.displayName,
                            unreadCount: group.unReadMsgCnt,
                            messageList: clientMessageList
                        };
                    },
                        // runs when the active group changes. deselects all messages
                    activeGroupChanged: function (event) {
                        var newGroup = event.args[0],
                            oldGroup = event.args[1];

                        internal.activeGroup = newGroup;
                        internal.lastActiveGroup = oldGroup;
                        internal.clearSelectedMessages(oldGroup);
                    },
                        // clears the selected messages
                    clearSelectedMessages: function (oldSelection) {
                        if (oldSelection !== null) {
                            oldSelection.messageList.setSelected();
                        }
                    },
                        // helper function to return the state of the checkbox based on the selected and total counts
                    checkboxState: function (selected, total) {
                        if (selected === 0) {
                            return 'none';
                        } else if (selected === total) {
                            return 'all';
                        } else {
                            return 'some';
                        }
                    },
                        // updates the active message (sets the "active" property)
                    updateActiveMessage: function (event) {
                        var newMessage = event.args[0],
                            oldMessage = event.args[1];

                            // need to check in case of setting active = null
                        if (newMessage) {
                            newMessage.active = true;
                            internal.markReadWorkflow.dispatch([[newMessage]]);
                        }
                            // need to check in case of setting active when no previous active message
                        if (oldMessage) {
                            oldMessage.active = false;
                        }
                    },

                        // sorts the groups by displayName (with "General" at the top)
                    sortGroups: function (company) {
                        return company.displayName === Constants.GENERAL_DISPLAY_NAME ? '' : company.displayName;
                    },
                        // contains lots of info necessary for the sort
                    sortHelpers: {
                            // default sort order
                        defaults: {
                            sorts: {
                                date: -1,
                                from: 1,
                                read: 1,
                                subject: 1,
                                priority: -1
                            },
                            properties: ['date', 'priority', 'from', 'subject'],
                            order: -1
                        },
                            // the generic A/B sort function
                        genericSortFn: function (key, mult, converter, a, b) {
                            var aa = a,
                                bb = b;
                            if (aa === bb) {
                                return 0;
                            }
                            if (aa === undefined) {
                                return mult;
                            }
                            if (bb === undefined) {
                                return -mult;
                            }
                            aa = aa[key] ? converter(aa[key]).toString().toLowerCase() : '';
                            bb = bb[key] ? converter(bb[key]).toString().toLowerCase() : '';
                            if (_.isEqual(aa, bb)) {
                                return 0;
                            }
                            return mult * (aa < bb ? -1 : 1);
                        },
                            // maps from property strings to multiplicative values for sorting
                        converterMaps: {
                            priority: function (val) {
                                var highPriority = 3,
                                    lowPriority = -3;
                                switch (val) {
                                    case Constants.PRIORITY.HIGH :
                                        return highPriority;
                                    case Constants.PRIORITY.NORMAL :
                                        return 0;
                                    case Constants.PRIORITY.LOW :
                                        return lowPriority;
                                    default:
                                        return 0;
                                }
                            },
                            readStatus: function (val) {
                                var unreadStatus = 3;
                                switch (val) {
                                    case Constants.STATUS.UNREAD :
                                        return unreadStatus;
                                    case Constants.STATUS.READ :
                                        return 0;
                                    default:
                                        return 0;
                                }
                            }
                        }
                    },
                    makeSimpleState: function makeSimpleState(globalState) {
                        return {
                            mode: globalState.mode,
                            client: _.get(globalState, 'clients[0]._clientPlatformGroup.displayId', '')
                        };
                    },
                    checkIfStateChanged: function checkIfStateChanged(newState, oldState) {
                        var prevState = oldState || internal.currentState;
                        return !prevState || prevState.mode !== newState.mode || prevState.client !== newState.client;
                    }
                };

                // pulled into separate function so can be re-used in both refresh workflows and now
                // by the load workflow once the real values come back from the server (so any differences
                // between real values and cached values are updated)
                /**
                 *
                 * @returns {{clearModel: (clearModel|{execute, rollback}), processMessages: (processMessages|{execute}), cleanupAfterRefresh: {execute: cleanupAfterRefresh.execute}}} - post refresh steps
                 */
                function getPostRefreshSteps() {
                    return {
                        clearModel: internal.workflowSteps.clearModel,
                        processMessages: internal.workflowSteps.processMessages,
                        cleanupAfterRefresh: {
                            execute: function execute() {
                                var activeGroup = internal.messageGroups.getActive(),
                                    totalLength,
                                    selectedCount;
                                if (activeGroup) {
                                    totalLength = activeGroup.messageList.getSize();
                                    selectedCount = activeGroup.messageList.getSelected().length;
                                    external.checkboxState = internal.checkboxState(selectedCount, totalLength);
                                } else {
                                    external.checkboxState = internal.checkboxState(0, 1);
                                }
                                activeGroup = internal.messageGroups.getItems()[1];
                                if (activeGroup) {
                                    internal.messageGroups.setActive(activeGroup); // temporary upgrade active group to current work flow
                                } else {
                                    // Adding to support Internal user where user have no clients except for General Messages
                                    external.setActiveGroup(internal.messageGroups.getItems()[0]); // temporary upgrade active group to current work flow
                                }
                                // we deleted the ModelLists in the clearModel step, so we need to re-apply
                                // the previously selected sort once the new data arrives and the active group
                                // is set
                                external.sortMessages();
                            }
                        }
                    };
                }

                /**
                 * @param {boolean} forceLoad - forceLoad
                 * @returns {{execute: execute}} - returns updated data model from service
                 */
                function getMessagesRemoteCall(forceLoad) {
                    return {
                        execute: function () {
                            var state = this.results.setupRemoteCall,
                                clientIds = _.map(state.clients, '_clientPlatformGroup.displayId'),
                                self = this; // context in which to run our post-refresh steps
                            if (!forceLoad) {
                                // once real values to come back from the server, we need to update the internal model;
                                // we will dispatch the post-refresh workflow steps to ensure our logic is the same as if the
                                // user manually refreshed the message list.
                                // BTW, using 3 separate workflows, each with different post-load steps to keep the model
                                // consistent...is just silly. we could instead talk directly to the data service, merge
                                // whatever it has with a ModelList, and rely on collection-changed event handlers to perform
                                // whatever massaging needs to happen...
                                MessageListDataService.observeMessages(state.mode, clientIds)
                                // wait until the next tick (gives MessageListDataService.isLoaded time to be set)
                                    .observeOn(Rx.Scheduler.default /* i.e. process.nextTick or window.setTimeout */)
                                    // wait until the real server results have been returned
                                    .skipWhile(function isNotLoaded() {
                                        return !MessageListDataService.isLoaded;
                                    })
                                    .take(1 /* and then auto-unsubscribe */)
                                    .subscribe(function realValuesReturned(results) {
                                        // we're basically faking out a separate workflow dispatch, one
                                        // that has access to the results of this workflow (by updating
                                        // the results object for this step and passing the current context
                                        // to each step's execute method). the separate 'workflow' we are
                                        // invoking is the same set of steps that occur after a refresh,
                                        // in order to keep the UI updated
                                        self.results.getMessages = results;
                                        _.reduce(getPostRefreshSteps(), function combineStepsIntoPromise(result, step) {
                                            return result.then(function runNextRefreshStep() {
                                                return Promise.call(_.bind(step.execute, self));
                                            });
                                        }, Promise.resolve()).catch(/* swallow */).done();
                                    });
                            }
                            return ProgressService.loading.registerActivity('paychex.app.landing.getMessages', MessageListDataService.getMessages(state.mode, clientIds, forceLoad));
                        }
                    };
                }

                // workflow steps objects
                internal.workflowSteps = {
                    // determine what type of remote call to make, either get all messages or selected message
                    setupRemoteCall: {
                        execute: function () {
                            return $q(function promiseResolve(resolve) {
                                var unsub = GlobalStateService.subscribe(function unsubscribeFn(state) {
                                    unsub();
                                    resolve(state);
                                });
                            });
                        }
                    },
                    // clears the model (could be a pre-req for certain steps)
                    clearModel: {
                        execute: function () {
                            this.previousMessageGroups = internal.messageGroups.getItems();
                            internal.messageGroups.removeAll();
                        },
                        rollback: function () {
                            internal.messageGroups.add(this.previousMessageGroups);
                        }
                    },
                    clearModelWithCondition: {
                        execute: function () {
                            if (GlobalStateService.getAvailable('clients').length > Constants.MAX_CLIENTS) {
                                this.previousMessageGroups = internal.messageGroups.getItems();
                                internal.messageGroups.removeAll();
                            }
                        },
                        rollback: function () {
                            internal.messageGroups.add(this.previousMessageGroups);
                        }
                    },
                    restoreActiveMessage: {
                        execute: function () {
                            if (internal.activeGroup) {
                                internal.activeGroup.messageList.trigger('active-changed', internal.activeGroup.messageList.getActive());
                            }
                        }
                    },
                    getCachedMessages: {
                        execute: function () {
                            if (!external.allMessages) {
                                var state = this.results.setupRemoteCall,
                                    clients = GlobalStateService.getAvailable('clients'),
                                    clientIds = [];
                                _.forEach(clients, function pushId(client) {
                                    clientIds.push(client._clientPlatformGroup.displayId);
                                });
                                if (clients.length > Constants.MAX_CLIENTS) {
                                    // only get messages for the first client
                                    return ProgressService.loading.registerActivity('paychex.app.landing.getMessages', MessageListDataService.getMessages(state.mode, [state.clients[0]._clientPlatformGroup.displayId]));
                                } else {
                                    // get messages for the clients identified in the setupRemoteCall step
                                    external.allMessages = MessageListDataService.getMessages(state.mode, clientIds);
                                    return ProgressService.loading.registerActivity('paychex.app.landing.getMessages', external.allMessages);
                                }
                            } else {
                                return ProgressService.loading.registerActivity('paychex.app.landing.getMessages', external.allMessages);
                            }
                        }
                    },
                    // process the messages after a refresh
                    processMessages: {
                        execute: function () {
                            var mode = GlobalStateService.getState().mode,
                                adminMsgModes = ['admin', 'support', 'applications'],
                                typeOfMessages;
                            typeOfMessages = _.includes(adminMsgModes, mode) ? this.results.getMessages.admin : this.results.getMessages.employee;
                            _.forEach(typeOfMessages.companyList, function messageProcessor(group) {
                                if (group.clientId === null) {
                                    group.clientId = Constants.GENERAL_ID;
                                }
                                // TODO: remove this check once the remote has been updated
                                if (group.messages === null) {
                                    group.messages = [];
                                }

                                _.forEach(group.messages, function setMessageId(message) {
                                    message.clientId = group.clientId;
                                });

                                var currentGroup = external.getMessageGroupById(group.clientId);

                                // if it's new, create a new object and add to the model
                                if (currentGroup === null) {
                                    internal.messageGroups.add(internal.createGroupObject(group));
                                } else {
                                    currentGroup.messageList.load(group.messages);
                                }
                            });
                        }
                    },
                    // deselect all messages
                    deselectAll: {
                        execute: function () {
                            var previousSelection = [];
                            if (internal.activeGroup !== null) {
                                previousSelection = internal.activeGroup.messageList.getSelected();
                                internal.activeGroup.messageList.setSelected();
                            }
                            return previousSelection;
                        }
                    },
                    // setup the handlers on the individual group
                    setupGroupHandlers: {
                        execute: function () {
                            // only set the handlers once
                            if (!internal.groupHandlersSet) {
                                internal.messageGroups.on('select-message', external.selectMessage);
                                internal.messageGroups.on('collection-changed, items-modified', internal.updateStaticModel);

                                // MAYBE: (or just put in controller, iterating through groups):
                                internal.messageGroups.on('sort-messages', internal.sortGroups);

                                // clear selection on tab changed
                                internal.messageGroups.on('active-changed', internal.activeGroupChanged);

                                internal.groupHandlersSet = true;
                            }

                            // trigger setup of model
                            internal.messageGroups.trigger('collection-changed');

                            external.sortMessages(internal.sortHelpers.defaults.properties, internal.sortHelpers.defaults.order);
                        }
                    },
                    // filter out the messages that are already read
                    getReadMessages: {
                        execute: function (messages) {
                            var result = _.attempt(function filterUnreadMessages(){
                                return _.filter(messages, {readStatus: Constants.STATUS.UNREAD});
                            });
                            if(_.isError(result)){
                                return [];
                            }
                            return result;
                        }
                    },
                    // mark the messages as read
                    markMessagesReadRemoteCall: {
                        execute: function () {
                            var messagesToMark = this.results.getReadMessages;
                            if (messagesToMark.length > 0) {
                                return MessageListDataService.markMessagesRead(messagesToMark);
                            }
                        }
                    },
                    // filter out the messages that are already unread
                    getUnreadMessages: {
                        execute: function (messages) {
                            var result = _.attempt(function filterReadMessages(){
                                return _.filter(messages, {readStatus: Constants.STATUS.READ});
                            });
                            if(_.isError(result)){
                                return [];
                            }
                            return result;
                        }
                    },
                    // mark the messages as unread
                    markMessagesUnreadRemoteCall: {
                        execute: function () {
                            var messagesToMark = this.results.getUnreadMessages;
                            if (messagesToMark.length > 0) {
                                return MessageListDataService.markMessagesUnread(messagesToMark);
                            }
                        }
                    },
                    // check if there are multiple messages selected prior to deletion
                    checkIfMultipleMessagesSelected: {
                        execute: function (messages) {
                            this.activeGroupForDelete = internal.activeGroup;
                            if (messages.length > 1) {
                                return INotificationService.displaySingleMessage({message: new MessageStatus174()}).then(function checkDeleteOperation(result) {
                                    if (result.value !== MessageStatus174.YES) {
                                        return $q.reject('User canceled the delete operation');
                                    }
                                    // else continue
                                });
                            } else if (messages.length < 1) {
                                this.workflow.cancel();
                            }
                            // one message is the empty case
                        }
                    },
                    // remove the messages from the list (and the DOM). This is run in parallel to the actual deletion call,
                    // and if that call fails, this change will be rolled back
                    removeMessagesFromDOM: {
                        execute: function (messages) {
                            this.deletableMessages = _.filter(messages, {deletable: true});
                            this.activeGroupForDelete.messageList.setSelected(
                                _.difference(this.activeGroupForDelete.messageList.getSelected(), this.deletableMessages)
                            );
                            this.activeGroupForDelete.messageList.remove(this.deletableMessages);
                        },
                        rollback: function () {
                            this.activeGroupForDelete.messageList.add(this.deletableMessages);
                        },
                        failure: function () {
                            this.activeGroupForDelete.messageList.setSelected();
                        }
                    },
                    // check if any of the messages can not be deleted
                    promptForUndeletableMessage: {
                        execute: function (messages) {
                            if (this.deletableMessages.length < messages.length) {
                                var self = this;
                                return INotificationService.displaySingleMessage({message: new MessageStatus173()}).then(function setDeletableMessages() {
                                    self.activeGroupForDelete.messageList.setSelected();
                                    return undefined; /*added to get rid of paychex/consistent-return error*/
                                });
                            }
                        }
                    },
                    // make the remote call to delete the messages
                    makeDeleteRemoteCall: {
                        execute: function () {
                            if (this.deletableMessages.length > 0) {
                                return MessageListDataService.deleteMessages(this.deletableMessages);
                            }
                        }
                    }
                };
                // Get Messages
                internal.getMessageWorkflow = Workflows.create(Constants.NAMESPACE + '.get-message-workflow')
                    .addStep('clearModel', internal.workflowSteps.clearModel)
                    .addStep('setupRemoteCall', internal.workflowSteps.setupRemoteCall)
                    .addStep('getMessages', ['clearModel', 'setupRemoteCall'], getMessagesRemoteCall(false))
                    .addStep('processMessages', ['getMessages'], internal.workflowSteps.processMessages)
                    .addStep('setupGroupHandlers', ['processMessages'], internal.workflowSteps.setupGroupHandlers);

                // Refresh the message list
                internal.refreshMessagesWorkflow = Workflows.create(Constants.NAMESPACE + '.refresh-message-workflow')
                    .addStep('deactivate', external.setActiveMessage)
                    .addStep('setupRemoteCall', internal.workflowSteps.setupRemoteCall)
                    .addStep('getMessages', ['setupRemoteCall'], getMessagesRemoteCall(true))
                    .addSequence(['getMessages'], getPostRefreshSteps());

                /* Cache messages after the initial remote call to avoid further calls when the My Account / Company modes are switched
                 *  Use Local Refresh to re-paint the MessageList upon switching modes
                 * */
                internal.localRefreshMessagesWorkflow = Workflows.create(Constants.NAMESPACE + '.localRefresh-message-workflow')
                    .addStep('deactivate', external.setActiveMessage)
                    .addStep('setupRemoteCall', internal.workflowSteps.setupRemoteCall)
                    .addStep('getMessages', ['setupRemoteCall'], internal.workflowSteps.getCachedMessages)
                    .addSequence(['getMessages'], getPostRefreshSteps());

                internal.refreshMessagesWorkflow.log = Logger.get('refresh', {level: 'ALL'});

                // Delete Messages
                internal.deleteWorkflow = Workflows.create(Constants.NAMESPACE + '.delete-workflow')
                    .addStep('checkIfMultipleMessages', internal.workflowSteps.checkIfMultipleMessagesSelected)
                    .addStep('removeFromDOM', ['checkIfMultipleMessages'], internal.workflowSteps.removeMessagesFromDOM)
                    .addStep('promptForUndeletableMessage', ['removeFromDOM'], internal.workflowSteps.promptForUndeletableMessage)
                    .addStep('makeRemoteCall', ['checkIfMultipleMessages'], internal.workflowSteps.makeDeleteRemoteCall)
                    .addStep('deselectAll', ['promptForUndeletableMessage'], internal.workflowSteps.deselectAll);

                internal.markReadWorkflow = Workflows.create(Constants.NAMESPACE + '.mark-read-workflow')
                    .addStep('getReadMessages', internal.workflowSteps.getReadMessages)
                    .addStep('makeRemoteCall', ['getReadMessages'], internal.workflowSteps.markMessagesReadRemoteCall)
                    .addStep('updateStatus', ['getReadMessages'], function alterReadStatus() {
                        return {
                            execute: function (messages) {
                                _.forEach(messages, function readStatus(message) {
                                    message.readStatus = Constants.STATUS.READ;
                                });
                                internal.messageGroups.trigger('items-modified');
                                return messages;
                            },
                            failure: function (messages) {
                                _.forEach(messages, function unreadStatus(message) {
                                    message.readStatus = Constants.STATUS.UNREAD;
                                });
                                return messages;
                            }
                        };
                    });

                // Mark Messages Unread
                internal.markUnreadWorkflow = Workflows.create(Constants.NAMESPACE + '.mark-unread-workflow')
                    .addStep('getUnreadMessages', internal.workflowSteps.getUnreadMessages)
                    .addStep('makeRemoteCall', ['getUnreadMessages'], internal.workflowSteps.markMessagesUnreadRemoteCall)
                    .addStep('updateStatus', ['getUnreadMessages'], function markMessagesUnread() {
                        return {
                            execute: function (messages) {
                                _.forEach(messages, function unreadSuccess(message) {
                                    message.readStatus = Constants.STATUS.UNREAD;
                                });
                                internal.messageGroups.trigger('items-modified');
                                return messages;
                            },
                            failure: function (messages) {
                                _.forEach(messages, function unreadFailure(message) {
                                    message.readStatus = Constants.STATUS.READ;
                                });
                                return messages;
                            }
                        };
                    });

                // Mark Messages Read
                internal.markReadAndDeselectWorkflow = Workflows.create(Constants.NAMESPACE + '.mark-read-and-deselect-workflow')
                    .addStep('markRead', internal.markReadWorkflow)
                    .addStep('deselectAll', ['markRead'], internal.workflowSteps.deselectAll);

                // Mark Messages Read
                internal.markUnreadAndDeselectWorkflow = Workflows.create(Constants.NAMESPACE + '.mark-unread-and-deselect-workflow')
                    .addStep('markUnread', internal.markUnreadWorkflow)
                    .addStep('deselectAll', ['markUnread'], internal.workflowSteps.deselectAll);

                internal.messageGroups = new ModelList();

                internal.init = function initGetMessageWorkflow() {

                    internal.messageGroups.sortBy(internal.sortGroups);

                    internal.activeGroup = null;
                    internal.groupHandlersSet = false;

                    external.getMessagePromise = UserInfoService.getUserInfo()
                        .then(function startGetMessageWorkflow(userInfo) {
                            helper = new UserInfoHelper(userInfo);
                            // start the getMessageWorkflow
                            return readyToLoadMessagePromise.promise;
                        }).then(function messageLoader() {
                            return external.loadMessages();
                        });
                };

                ParseMarkupService.addCustomFormatter('href', function customFormatter(val, element) {
                    if (val === 'event:messageLinkEvent') {
                        var target = $(element).attr('target'), // eslint-disable-line jquery/no-attr
                        /* var mode = helper.getModeFromAppId(target);*/ // refactor
                            uniqueClientId = helper.getUniqueIdentifierFromDisplayId(currentGroupBeingProcessed);
                        // Substitute 'target' attr value to make the link opened in current page. Else it will open a new tab.
                        $(element).attr('target', '_self');// eslint-disable-line jquery/no-attr
                        /* var baseUrl = $(location).attr('href');//Retrieve current URL
                         baseUrl = baseUrl.substring(0, baseUrl.indexOf('#?mode'));//To cut the URL from the point we wish to substitute.
                         return baseUrl + '#?mode=' + mode + '&clients=' + uniqueClientId + '&app=' + target;*/ // refactor
                        /* jshint scripturl:true*/ // adding this line to disable JSHint warning
                        return 'javascript:loadApp({app:\"' + target + '\", clients:[\"' + uniqueClientId + '\"]})';
                    }
                    return val;
                });

                window.loadApp = function loadApp(data) {
                    UrlService.set(data);
                };



                internal.init();

                return external;
            };

        Constructor.$inject = ngDependencies;

        return Constructor;
    });

